import { CommonModule } from "@angular/common";
import { Component, Injector, OnInit, Input } from "@angular/core";
import { Router, RouterModule } from "@angular/router";
import { AuthService } from "../../../../services/auth.service";
import { VenViewModel } from "../../../../model/vens.model";
import {DEBOUNCE_TIME, extractErrorMessage, goToNextPage} from "../../../../common/common";
import {ApiService, CustomHttpParams} from "src/app/services/api.service";
import { NgxPaginationModule } from "ngx-pagination";
import { FormsModule } from "@angular/forms";
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import {BehaviorSubject, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {LoaderComponent} from "../../../common/loader/loader.component";

@Component({
  selector: "app-ven-index",
  standalone: true,
  imports: [RouterModule, CommonModule, NgxPaginationModule, FormsModule, LoaderComponent],
  templateUrl: "./ven-index.component.html",
  styleUrl: "./ven-index.component.scss",
})
export class VenIndexComponent implements OnInit {
  @Input() public isComponentImported: boolean = false;
  @Input() public elementsPerPage: number = 10;

  protected colspan = this.isComponentImported ? 8 : 19;

  // Search fields for VEN parameters
  public idSearch: string = '';
  public venIdSearch: string = '';
  public venNameSearch: string = '';
  public profileSearch: string = '';
  public modeSearch: string = '';
  public vtnIdSearch: string = '';
  public platformSearch: string = '';
  public reportEnabledSearch: string = '';
  public sendReportCancellationSearch: string = '';
  public groupsSearch: string = '';
  public resourcesSearch: string = '';
  public partiesSearch: string = '';
  public marketContextsSearch: string = '';
  public marketContextLabelSearch: string = '';
  public metadataReportsSearch: string = '';
  public pausedSearch: string = '';

  // Debounce search
  private unsubscribe = new Subject<void>();
  private searchParams$ = new BehaviorSubject<any>({
    id: '',
    ven_id: '',
    name: '',
    profile: '',
    mode: '',
    vtn_id: '',
    platform_id: '',
    report_enabled: '',
    send_report_cancellation_enabled: '',
    paused: '',
    groupId: '',
    resourceId: '',
    partyId: '',
    marketContext: '',
    marketContextLabel: '',
    metadataReportId: ''
  });

  protected isLoading: boolean = false;
  public isAdmin: boolean = false;
  public router: Router;
  public error: string | null = null;
  public banner: string | null = null;
  protected readonly Array = Array;

  protected readonly paginationId = 'vensPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();
  protected displayedVens: VenViewModel[] = [];

  constructor(
    private injector: Injector,
    private authService: AuthService,
    private venService: ApiService
  ) {
    this.router = injector.get(Router);
  }

  ngOnInit() {
    this.paginationModel = this.pagination.resetPagination(this.elementsPerPage);
    this.isAdmin = this.authService.isAdmin();

    // Setup debounced search
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.loadVens();
    });
  }

  pause(event: any, pause: boolean) {
    this.banner = "Updating VEN...";
    this.venService.editVen({id: event.id, paused: pause}).subscribe({
      next: () => {
        this.banner = null;
        this.loadVens();
      },
      error: (err) => {
        console.error(err);
        this.banner = null;
        this.error = extractErrorMessage(err, "Failed to update VEN.");
      }
    });
  }

  loadVens() {
    this.isLoading = true;
    this.error = null;
    const queryParams = this.buildSearchParams();

    this.venService.findVens(queryParams).subscribe({
      next: (response: VenViewModel[]) => {
        this.displayedVens = response.map((ven: VenViewModel) => this.buildVenRecordFromViewModel(ven));
        this.paginationModel = this.pagination.updateTotalItems(response.length);
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.error = extractErrorMessage(err, "Failed to reload VENs.");
        this.isLoading = false;
      }
    });
  }

  buildSearchParams(): CustomHttpParams {
    const params: CustomHttpParams = {
      'depths': JSON.stringify({
        "registration": this.isComponentImported ? 0 : 1,
        "vtn": 1,
        "groups": this.isComponentImported ? 0 : 1,
        "resources": this.isComponentImported ? 0 : 1,
        "parties": this.isComponentImported ? 0 : 1,
        "market_contexts": this.isComponentImported ? 0 : 1,
        "metadata_reports": this.isComponentImported ? 0 : 1
      }),
      'depth': 2,
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
    };

    if (this.idSearch) {
      params['id'] = this.idSearch.trim();
    }
    if (this.venIdSearch) {
      params['ven_id'] = this.venIdSearch.trim();
    }
    if (this.venNameSearch) {
      params['name'] = this.venNameSearch.trim();
    }
    if (this.profileSearch) {
      params['profile'] = this.profileSearch.trim();
    }
    if (this.modeSearch) {
      params['mode'] = this.modeSearch.trim();
    }
    if (this.vtnIdSearch) {
      params['vtn.vtn_id'] = this.vtnIdSearch.trim();
    }
    if (this.platformSearch) {
      params['platform_id'] = this.platformSearch.trim();
    }
    if (this.reportEnabledSearch) {
      params['report_enabled'] = this.reportEnabledSearch.trim();
    }
    if (this.sendReportCancellationSearch) {
      params['send_report_cancellation_enabled'] = this.sendReportCancellationSearch.trim();
    }
    if (this.pausedSearch) {
      params['paused'] = this.pausedSearch.trim();
    }
    if (this.groupsSearch) {
      params['groups.group_id'] = this.groupsSearch.trim();
    }
    if (this.resourcesSearch) {
      params['resources.resource_id'] = this.resourcesSearch.trim();
    }
    if (this.partiesSearch) {
      params['parties.part_id'] = this.partiesSearch.trim();
    }
    if (this.marketContextsSearch) {
      params['market_contexts.market_context'] = this.marketContextsSearch.trim();
    }
    if (this.marketContextLabelSearch) {
      params['market_contexts.label'] = this.marketContextLabelSearch.trim();
    }
    if (this.metadataReportsSearch) {
      params['metadata_reports.report_specifier_id'] = this.metadataReportsSearch.trim();
    }

    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination(this.elementsPerPage);
    this.searchParams$.next({
      id: this.idSearch,
      ven_id: this.venIdSearch,
      name: this.venNameSearch,
      profile: this.profileSearch,
      mode: this.modeSearch,
      vtn_id: this.vtnIdSearch,
      platform_id: this.platformSearch,
      report_enabled: this.reportEnabledSearch,
      send_report_cancellation_enabled: this.sendReportCancellationSearch,
      paused: this.pausedSearch,
      'groups.group_id': this.groupsSearch,
      'resources.resource_id': this.resourcesSearch,
      'parties.part_id': this.partiesSearch,
      'market_contexts.market_context': this.marketContextsSearch,
      'market_contexts.label': this.marketContextLabelSearch,
      'metadata_reports.report_specifier_id': this.metadataReportsSearch
    });
  }

  private buildVenRecordFromViewModel(ven: VenViewModel) {
    return {
      ...ven,
      mode: ven.mode || 'poll',
      platform: ven.platform,
      group: Array.isArray(ven.groups) ? ven.groups : [],
      resources: Array.isArray(ven.resources) ? ven.resources : [],
      parties: Array.isArray(ven.parties) ? ven.parties : [],
      market_contexts: Array.isArray(ven.market_contexts) ? ven.market_contexts : [],
      metadata_report: Array.isArray(ven.metadata_reports) ? ven.metadata_reports : []
    };
  }

  nextPage(event: any, url: string, router: Router) {
    return goToNextPage(event, url, router);
  }

  toggle_vtn(ven: any) {
    this.isLoading = true;
    this.banner = "Switching VTN...";
    this.venService.togglePrimary({id: ven.id}).subscribe({
      next: () => {
        this.isLoading = false;
        this.banner = null;
        this.loadVens();
      },
      error: (err) => {
        this.isLoading = false;
        console.error(err);
        this.banner = null;
        this.error = extractErrorMessage(err, "Failed to toggle VTN.");
      }
    });
  }

  delete(event: any) {
    if (confirm('Are you sure you want to delete this VEN?')) {
      this.isLoading = true;
      this.venService.deleteVen(event.id).subscribe({
        next: () => {
          this.isLoading = false;
          this.loadVens();
        },
        error: (err) => {
          this.isLoading = false;
          console.error(err);
          this.error = extractErrorMessage(err, "Failed to delete VEN.");
        }
      });
    }
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.loadVens();
  }
}
